import { Component, inject, OnDestroy, signal, computed, Signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router } from '@angular/router';
import { GroupCellRendererComponent } from '@components/group-cell-renderer/group-cell-renderer.component';
import { BaseDataGridComponent } from '@components/base-data-grid.component';
import { dateFormattingOptions } from '@domain/formatting';
import { SlaughterhouseItem } from '@hjc/masterdata-types';
import { TranslateModule } from '@ngx-translate/core';
import { PartnersService } from '@services/partners.service';
import { ColDef, RowClickedEvent, themeAlpine, GridApi } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import Fuse from 'fuse.js';
import { debounceTime, Subscription } from 'rxjs';
import { PageHeaderComponent } from '@components/page-header/page-header.component';

@Component({
  selector: 'md-view-slaughterhouses-page',
  imports: [PageHeaderComponent, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, AgGridAngular, ReactiveFormsModule, TranslateModule],
  templateUrl: './view-slaughterhouses-page.component.html',
  styleUrl: './view-slaughterhouses-page.component.scss',
  standalone: true
})
export class ViewSlaughterhousesPageComponent extends BaseDataGridComponent implements OnDestroy {
  private partnersService = inject(PartnersService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private gridApi = signal<GridApi<SlaughterhouseItem> | null>(null);

  isLoading = signal(false);
  isExporting = signal(false);
  allSlaughterhouses = signal<SlaughterhouseItem[]>([]);
  displaySlaughterhouses = signal<SlaughterhouseItem[]>([]);

  searchInput = new FormControl<string>('', { nonNullable: true });
  searchTermSubscription?: Subscription;
  fuse?: Fuse<SlaughterhouseItem>;
  theme = themeAlpine;

  columnDefs: Signal<ColDef<SlaughterhouseItem>[]> = computed(() => {
    const getTranslatedHeader = (key: string) => (this.translationsReady() ? this.translate.instant(key) : key);

    return [
      { headerName: getTranslatedHeader('FIELDS.IDENTIFIER'), field: 'id', sortable: true, flex: 2 },
      { headerName: getTranslatedHeader('INFO.NAME'), field: `name.${this.lang()}`, sortable: true, flex: 2 },
      {
        headerName: getTranslatedHeader('INFO.BY_PRODUCTS'),
        valueGetter: p => p.data?.byproducts.map(bp => bp.productName[this.lang()]),
        cellRenderer: GroupCellRendererComponent,
        sortable: false,
        flex: 3,
        autoHeight: true
      },
      {
        headerName: getTranslatedHeader('INFO.FILLING_SPOTS'),
        valueGetter: p => p.data?.byproducts.map(bp => bp.fillingSpotId),
        cellRenderer: GroupCellRendererComponent,
        sortable: false,
        flex: 2,
        autoHeight: true
      },
      { headerName: getTranslatedHeader('FIELDS.STREET_ADDRESS'), field: 'address', sortable: true, flex: 2 },
      { headerName: getTranslatedHeader('FIELDS.POSTAL_CODE'), field: 'zip', sortable: true, flex: 2 },
      { headerName: getTranslatedHeader('FIELDS.CITY'), field: 'city', sortable: true, flex: 2 },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_BY'), field: 'createdBy', sortable: true, filter: true, flex: 2 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_AT'),
        field: 'createdAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 2
      },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_BY'), field: 'updatedBy', sortable: true, filter: true, flex: 2 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_AT'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 2
      }
    ];
  });

  constructor() {
    super();
    this.translate.onLangChange.subscribe(() => this.initFuse());
    this.init();
  }

  async init() {
    this.isLoading.set(true);
    const slaughterhouses = (await this.partnersService.getSlaughterhousePartners())?.data;
    this.allSlaughterhouses.set(slaughterhouses);
    this.displaySlaughterhouses.set(slaughterhouses);
    this.initFuse();
    this.searchTermSubscription = this.searchInput.valueChanges.pipe(debounceTime(300)).subscribe(term => this.search(term));
    this.isLoading.set(false);
  }

  initFuse() {
    this.fuse = new Fuse<SlaughterhouseItem>(this.allSlaughterhouses(), {
      keys: ['id', `name.${this.lang()}`, 'address', 'postalCode', 'city', 'fillingSpots', `byProducts.${this.lang()}`],
      threshold: 0.3
    });
  }

  search(term: string) {
    if (!this.fuse) return;
    this.displaySlaughterhouses.set(term && term.length > 0 ? this.fuse.search(term).map(r => r.item) : this.allSlaughterhouses());
  }

  rowOnClick(event: RowClickedEvent<SlaughterhouseItem>) {
    const rowItemId = event.data?.id;
    if (rowItemId) {
      this.router.navigate([rowItemId], { relativeTo: this.route });
    }
  }

  onGridReady(params: { api: GridApi<SlaughterhouseItem> }) {
    this.gridApi.set(params.api);
  }

  async exportToExcel() {
    const api = this.gridApi();
    if (!api) return;

    await this.exportGridToExcel(api, this.columnDefs(), 'slaughterhouses', this.isExporting);
  }

  ngOnDestroy() {
    this.searchTermSubscription?.unsubscribe();
  }
}
